import {Modal, Flex, Button, Group} from '@mantine/core';
import React, {useState} from 'react';

import {getChemicalFileContent, guessChemicalFileFormat} from 'chemistry/ChemicalFileUtil';
import {
  ChemicalStructureDrawer,
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {
  CHEMICAL_FILE_FORMAT_TO_MARVIN_FORMAT,
  isMarvinCompatibleFileFormat,
  MarvinChemicalFormat,
} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import ModalFooter from 'ui-library/modals/ModalFooter';

export interface StructureWithFormat {
  structure: ChemicalStructureDrawerValue;
  format: MarvinChemicalFormat;
}

export async function getStructureWithFormatFromFile(file: File): Promise<StructureWithFormat> {
  const fileFormat = guessChemicalFileFormat(file);
  if (!isMarvinCompatibleFileFormat(fileFormat)) {
    throw new Error(`Unsupported file format for reaction sketcher: ${fileFormat}`);
  }
  const fileContent = await getChemicalFileContent(fileFormat, file);
  return {structure: fileContent, format: CHEMICAL_FILE_FORMAT_TO_MARVIN_FORMAT[fileFormat]};
}

interface ReactionSketcherModalProps {
  onClose: () => void;
  onSubmit: (newMarvinStructure: string) => Promise<void>;
  initialStructureData?: StructureWithFormat;
}

export default function ReactionSketcherModal({
  onClose,
  onSubmit,
  initialStructureData,
}: ReactionSketcherModalProps): JSX.Element {
  const [marvinStructure, setMarvinStructure] = useState<ChemicalStructureDrawerValue>(null);
  const [hasReactionContents, setHasReactionContents] = useState(false);

  return (
    <Modal
      title="Reaction Sketcher"
      opened={true}
      onClose={onClose}
      size="xl"
      styles={{
        content: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          flex: 1,
        },
      }}
    >
      <Flex direction="column" h="100%">
        <ChemicalStructureDrawer
          mode={ChemicalStructureDrawerMode.REACTION}
          onMoleculeChange={setMarvinStructure}
          setHasReactionContents={setHasReactionContents}
          initialValue={initialStructureData?.structure ?? null}
          inputFormat={initialStructureData?.format}
          outputFormat={MarvinChemicalFormat.MRV}
        />
        <ModalFooter>
          <Group position="right">
            <Button type="button" onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!hasReactionContents}
              onClick={() => {
                onSubmit(marvinStructure!);
              }}
            >
              Save
            </Button>
          </Group>
        </ModalFooter>
      </Flex>
    </Modal>
  );
}
